import { useEffect, useState } from 'react'

/**
 * Hook that debounces a value by a specified delay.
 * Useful for delaying API calls or expensive operations until user stops typing.
 *
 * @param value - The value to debounce
 * @param delay - The debounce delay in milliseconds (default: 1500ms)
 * @returns The debounced value
 *
 * @example
 * const [searchTerm, setSearchTerm] = useState('')
 * const debouncedSearch = useDebounce(searchTerm, 1500)
 *
 * useEffect(() => {
 *   // This effect only runs 1500ms after the user stops typing
 *   searchApi(debouncedSearch)
 * }, [debouncedSearch])
 */
export function useDebounce<T>(value: T, delay = 1500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    // Cancel the timeout if value changes (also on delay change or unmount)
    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

export default useDebounce
